import React from 'react';
import { styled } from '@mui/material';
import { LineChart as LineChartGraph, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { defaultChartPalette } from '../../../../utils/palette';
import { findStringKey, getNumberKeys, getColorByPosition, findKeyOfMaxNumber, filterZeroKeys } from './lineChartUtils';
import TooltipBody, { StringSeries } from '../TooltipBody';

export type MixedDataPoint = {
  [key: string]: string | number;
};

interface CartesianGridType {
  verticalLines: boolean;
  horizontalLines: boolean;
}

export interface LineChartProps {
  type: 'lineChart';
  data: MixedDataPoint[];
  palette?: string[];
  cartesianGrid?: CartesianGridType;
}

const LineChart: React.FC<LineChartProps> = ({ data, palette, cartesianGrid }) => {
  const noZeroValueData = filterZeroKeys(data);
  const stringKey = findStringKey(noZeroValueData);
  const numberKeys = getNumberKeys(noZeroValueData);

  const series: StringSeries = numberKeys.map((key, index) => ({
    name: key,
    color: (palette && palette[index]) || getColorByPosition(defaultChartPalette, index) || '',
  }));

  return (
    <ResponsiveContainerCustomer height={300}>
      <LineChartGraph data={noZeroValueData}>
        <CartesianGrid strokeDasharray="1 3" vertical={cartesianGrid?.verticalLines} horizontal={cartesianGrid?.horizontalLines} />
        {numberKeys.map((key, index) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={(palette && palette[index]) || getColorByPosition(defaultChartPalette, index)}
            dot={true}
            strokeWidth={2}
          />
        ))}
        <XAxis dataKey={stringKey} />
        <YAxis dataKey={findKeyOfMaxNumber(noZeroValueData)} />
        <Legend verticalAlign="top" align="right" />
        <Tooltip content={<TooltipBody series={series} />} />
      </LineChartGraph>
    </ResponsiveContainerCustomer>
  );
};

export default LineChart;

const ResponsiveContainerCustomer = styled(ResponsiveContainer)(({ theme }) => ({
  marginLeft: `-${theme.spacing(2.5)}`,
}));
